/** Doc → ag-psd assembly. Shared layer isolation lives in layered-export/plan-layers.ts. */
import type { Layer as AgPsdLayer, PixelData, Psd } from "ag-psd";
import type { Doc } from "@hightouch/lib/ad-studio/html-editor/model";
import {
  planLayers,
  type LayerPlan,
} from "src/pages/ad-studio/layered-export/plan-layers";
import { trimTransparentPixels } from "src/pages/ad-studio/psd-export/alpha-trim";
import type { PsdFontResolver } from "@hightouch/lib/ad-studio/psd-export/psd-font";
import type { PsdFontMetricsResolver } from "@hightouch/lib/ad-studio/psd-export/psd-font-metrics";
import type { TextMeasurement } from "src/pages/ad-studio/psd-export/text-measure";
import { buildTextLayerEffects } from "src/pages/ad-studio/psd-export/text-effects";
import { buildTextLayerData } from "@hightouch/lib/ad-studio/psd-export/text-layer-data";
export type RasterizeDoc = (doc: Doc) => Promise<PixelData>;
/** Font naming, font metrics, and rendered measurements for the editable text data. */
export type PsdTextOptions = {
  resolveFont?: PsdFontResolver;
  measureFont?: PsdFontMetricsResolver;
  /** Rendered measurements by layer id; the baseline beats the metrics-derived one. */
  measurements?: Map<string, TextMeasurement>;
};
export async function buildPsdDocument(args: {
  doc: Doc;
  rasterizeDoc: RasterizeDoc;
  /** Flattened preview. Production passes a capture of the original markup so it matches the PNG export. */
  composite?: PixelData;
  textOptions?: PsdTextOptions;
}): Promise<Psd & { children: AgPsdLayer[] }> {
  const { doc, rasterizeDoc } = args;
  const children: AgPsdLayer[] = [];
  // Sequential: each capture inlines images and preloads fonts; parallelism stampedes the main thread.
  for (const plan of planLayers(doc)) {
    children.push(
      await assemblePlan(plan, rasterizeDoc, args.textOptions ?? {}),
    );
  }
  const composite = args.composite ?? (await rasterizeDoc(doc));
  return {
    width: Math.round(doc.width),
    height: Math.round(doc.height),
    children,
    imageData: composite,
  };
}

async function assemblePlan(
  plan: LayerPlan,
  rasterizeDoc: RasterizeDoc,
  textOptions: PsdTextOptions,
): Promise<AgPsdLayer> {
  if (plan.kind === "group") {
    const children: AgPsdLayer[] = [];
    for (const child of plan.children) {
      children.push(await assemblePlan(child, rasterizeDoc, textOptions));
    }
    const group: AgPsdLayer = {
      name: plan.name,
      opened: false,
      hidden: plan.hidden,
      opacity: plan.opacity,
      children,
    };
    if (plan.blendMode) group.blendMode = plan.blendMode;
    return group;
  }

  const layer: AgPsdLayer = {
    name: plan.name,
    hidden: plan.hidden,
    opacity: plan.opacity,
    ...trimmedPlacement(await rasterizeDoc(plan.captureDoc)),
  };
  if (plan.blendMode) layer.blendMode = plan.blendMode;
  if (plan.editableText) {
    const source = plan.editableText.layer;
    layer.text = buildTextLayerData(source, plan.editableText.parentTransform, {
      resolveFont: textOptions.resolveFont,
      measureFont: textOptions.measureFont,
      measuredBaseline:
        textOptions.measurements?.get(source.id)?.baseline ?? undefined,
    });
    layer.effects = buildTextLayerEffects(source);
  }
  return layer;
}

/** A capture as an ag-psd layer's bitmap and placement; nothing when it is fully transparent. */
export function trimmedPlacement(
  pixels: PixelData,
): Pick<AgPsdLayer, "left" | "top" | "imageData"> | undefined {
  const trimmed = trimTransparentPixels(pixels);
  if (!trimmed) return undefined;
  return {
    left: trimmed.left,
    top: trimmed.top,
    imageData: trimmed.imageData,
  };
}
